import { useEffect, useRef, useState } from 'react';
import type { ConfigType } from 'dayjs';
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { opTime } from 'utils/dates';
import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpTabs } from 'new-components/DLS/OpTabs/OpTabs';
import { useSelectorJs } from 'utils/customHooks';
import { heliumReceive, heliumSend } from 'utils/helpers';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { getOpParams } from 'utils/getOpParams';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';

import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import { toLocalByTimezone } from '../../../../utils/dates';
import { VisitorType } from '../../../../types/VisitorTypeTypes';
import {
  DATE_TIME_AM_PM_FORMAT,
  DATE_TIME_FORMAT,
} from '../../../../constants/dates';
import STATUS from '../../../../constants/status';
import { fetchVisits, VisitsState } from '../../../../store/slices/visitsSlice';
import { FormFieldWorkflow, VisitData } from '../../../../types/formFieldTypes';
import {
  fetchInvitationConfig,
  fetchInvitationCustomFields,
  VisitorInvitationState,
} from '../../../../store/slices/visitorInvitationSlice';
import { VisitorWorkflowFields } from '../../../../types/visitorWorkflowTypes';
import {
  searchVisitors,
  searchVisitorsToday,
  VisitorSearchState,
} from '../../../../store/slices/visitorSearchSlice';
import {
  CustomFieldsWorkflowResponse,
  ExcludeHostsResponse,
  HostUser,
  LocationWorkflowConfig,
  LocationWorkflowResponse,
  UsersResponse,
  VisitDataResponse,
  VisitorTypeWorkflowResponse,
} from '../../../../types/visitorsDrawerTypes';

interface ListVisitorTypesResponse {
  data?: VisitorType[] | null;
}

interface WorkflowFieldsResponse {
  data?: VisitorWorkflowFields[] | null;
}

interface EditVisitFormValues {
  company?: string | null;
  visitorType?: number;
  hostId?: number | null;
  purpose?: string | null;
  signIn?: string | null;
  signOut?: string | null;
  scheduleStart?: string | null;
  scheduleEnd?: string | null;
  [key: string]: unknown;
}

interface FieldChange {
  name: unknown;
  value?: ConfigType;
}

interface ModalFormProps {
  open: boolean;
  onClose: () => void;
  setLoading: (loading: boolean) => void;
}

const VisitorsEditModal = ({ open, onClose, setLoading }: ModalFormProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { userId: globalUserId } = useIdentityAndUser();
  const { isValid: hasAllVisitorsWrite } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];
  const invitationCustomFields = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationCustomFields']),
  ) as VisitorInvitationState['invitationCustomFields'];
  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as number | undefined;
  const lastSearchParams = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorSearch', 'lastSearchParams']),
  ) as VisitorSearchState['lastSearchParams'];

  const [hosts, setHosts] = useState<HostUser[]>([]);
  const [excludedHostIds, setExcludedHostIds] = useState<number[]>([]);
  const [visitorTypes, setVisitorTypes] = useState<VisitorType[]>([]);
  const [visitData, setVisitData] = useState<VisitData[]>([]);
  const [activeKey, setActiveKey] = useState<string>('details');
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [locationDefaultWorkflow, setLocationDefaultWorkflow] =
    useState<LocationWorkflowConfig>();
  const [locationVisitorTypes, setLocationVisitorTypes] = useState<
    VisitorType[]
  >([]);
  const [signInWorkflowFields, setSignInWorkflowFields] =
    useState<VisitorWorkflowFields>();
  const [customFieldsWorkflow, setCustomFieldsWorkflow] = useState<
    FormFieldWorkflow[]
  >([]);

  const isInitialLoad = useRef(true);

  const [form] = OpForm.useForm();
  const selectedVisitorTypeId = OpForm.useWatch('visitorType', form);

  useEffect(() => {
    if (open) {
      setActiveKey('details');
    }
  }, [open]);

  useEffect(() => {
    const fetchData = async () => {
      setIsModalLoading(true);
      try {
        const locationDefaultWorkflowResponse = (await heliumReceive(
          'locationConfig',
          [orgId, selectedVisit?.location?.id],
        )) as LocationWorkflowResponse;
        const locationVisitorTypesResponse = (await heliumReceive(
          'listAllVisitorTypeWorkflow',
          [orgId, selectedVisit?.location?.id],
        )) as ListVisitorTypesResponse;
        const locationVisitorTypeWorkflowResponse = (await heliumReceive(
          'describeVisitorTypeWorkflow',
          [orgId, selectedVisit?.location.id, selectedVisit?.visitorType.id],
        )) as VisitorTypeWorkflowResponse;

        setLocationDefaultWorkflow(locationDefaultWorkflowResponse.data?.[0]); // Default location workflow
        setLocationVisitorTypes(locationVisitorTypesResponse.data ?? []); // Visitor Types that contains workflows by visitor type

        const defaultWorkflowData = locationDefaultWorkflowResponse.data?.[0];
        const visitorTypeWorkflowData =
          locationVisitorTypeWorkflowResponse.data?.[0]?.workflow;

        const [usersRes, excludedHostsRes] = (await Promise.all([
          heliumReceive('listUsers', [orgId]),
          heliumReceive('listAllExcludeHosts', [orgId]),
        ])) as [UsersResponse, ExcludeHostsResponse];

        const activeUsers = (usersRes.data ?? []).filter(
          (u) => u.status === 'A',
        );
        const excludedIds = (excludedHostsRes.data ?? []).map(
          (ex) => ex.userId,
        );

        setHosts(activeUsers);
        setExcludedHostIds(excludedIds);

        const visitorTypeSignInWorkflowId =
          visitorTypeWorkflowData?.SignInWorkflowId;
        const signInWorkflowId =
          visitorTypeSignInWorkflowId !== undefined &&
          visitorTypeSignInWorkflowId !== -1
            ? visitorTypeSignInWorkflowId
            : defaultWorkflowData?.SignInWorkflowId;

        const visitorTypesResponse = (await heliumReceive('getVisitorTypes', [
          orgId,
        ])) as ListVisitorTypesResponse;
        setVisitorTypes(visitorTypesResponse.data ?? []);

        const customFieldsWorkflowResponse = (await heliumReceive(
          'getVisitorWorkflowFieldsCustom',
          [orgId, signInWorkflowId],
        )) as CustomFieldsWorkflowResponse;
        setCustomFieldsWorkflow(customFieldsWorkflowResponse.data ?? []);

        const visitDataResponse = (await heliumReceive('getVisitorData', [
          orgId,
          selectedVisit?.id,
        ])) as VisitDataResponse;
        setVisitData(visitDataResponse.data ?? []);

        if (invitationConfig === null) {
          dispatch(fetchInvitationConfig({ orgId }));
        }

        if (invitationCustomFields.length === 0) {
          dispatch(fetchInvitationCustomFields({ orgId }));
        }

        const signInWorkflowConfigResponse = (await heliumReceive(
          'getVisitorWorkflowFields',
          [orgId, signInWorkflowId],
        )) as WorkflowFieldsResponse;
        setSignInWorkflowFields(signInWorkflowConfigResponse.data?.[0]);
      } catch {
        // Failed to fetch data
      } finally {
        setIsModalLoading(false);
      }
    };
    fetchData();
  }, [
    orgId,
    selectedVisit?.id,
    dispatch,
    globalUserId,
    invitationConfig,
    invitationCustomFields.length,
    selectedVisit?.location.id,
    selectedVisit?.host.userId,
    selectedVisit?.visitorType.id,
  ]);

  useEffect(() => {
    const handleVisitorTypeChange = async () => {
      // Return if datas are not ready
      if (!selectedVisitorTypeId || locationVisitorTypes.length === 0) return;

      // Return if it's initial load (fields are already set during initial data setting)
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        return;
      }

      const selectedVisitorType = locationVisitorTypes.find(
        (v) => v.id === selectedVisitorTypeId,
      );

      const hasVisitorTypeSignInWorkflow = (locationVisitorTypes ?? []).some(
        (type) =>
          type.workflow?.SignInWorkflowId &&
          type.workflow.SignInWorkflowId !== -1,
      );

      // Return if selected visitor type doesn't exist in location visitor types or any workflow didn't get allocated to any visitor type
      if (!selectedVisitorType || !hasVisitorTypeSignInWorkflow) return;

      const workflowId = selectedVisitorType.workflow?.SignInWorkflowId;
      const finalWorkflowId =
        workflowId !== -1
          ? workflowId
          : locationDefaultWorkflow?.SignInWorkflowId;

      if (!finalWorkflowId) return;

      try {
        const fieldConfig = (await heliumReceive('getVisitorWorkflowFields', [
          orgId,
          finalWorkflowId,
        ])) as WorkflowFieldsResponse;
        setSignInWorkflowFields(fieldConfig.data?.[0]);

        const fieldCustom = (await heliumReceive(
          'getVisitorWorkflowFieldsCustom',
          [orgId, finalWorkflowId],
        )) as CustomFieldsWorkflowResponse;
        setCustomFieldsWorkflow(fieldCustom.data ?? []);

        if (hasVisitorTypeSignInWorkflow) {
          notificationService.show(
            'success',
            t('signInManuallyDrawer.notification.changeFieldsNotification'),
          );
        }
      } catch {
        notificationService.show(
          'error',
          t('signInManuallyDrawer.notification.changeFieldsError'),
        );
      }
    };
    handleVisitorTypeChange();
  }, [
    orgId,
    selectedVisitorTypeId,
    locationDefaultWorkflow,
    locationVisitorTypes,
    t,
    dispatch,
  ]);

  // Fallback: location timezone → browser timezone
  const tz =
    selectedVisit?.location?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getDisabledTime = (current: ConfigType | null) => {
    if (!current) return {};

    const nowInTz = opTime().tz(tz);

    // Convert current date to location timezone for comparison
    const currentDateStr = opTime(current).format('YYYY-MM-DD');
    const currentInTz = opTime.tz(currentDateStr, tz);
    const isToday = currentInTz.isSame(nowInTz, 'day');

    // For past dates, don't disable any time (all times are valid)
    if (!isToday) return {};

    const currentHour = nowInTz.hour();
    const currentMinute = nowInTz.minute();

    // For today, disable future hours and minutes (only past is valid)
    return {
      disabledHours: () =>
        Array.from({ length: 24 }, (_, i) => i).filter((h) => h > currentHour),
      disabledMinutes: (selectedHour: number) => {
        if (selectedHour > currentHour) {
          return Array.from({ length: 60 }, (_, i) => i);
        }
        if (selectedHour === currentHour) {
          return Array.from({ length: 60 }, (_, i) => i).filter(
            (m) => m > currentMinute,
          );
        }
        return [];
      },
    };
  };

  const initialValues: Record<string, unknown> = {
    company: selectedVisit?.company,
    locationId: selectedVisit?.location?.id,
    visitorType: selectedVisit?.visitorType?.id,
    hostId: selectedVisit?.host?.userId,
    purpose: selectedVisit?.purpose,
    signIn: toLocalByTimezone(selectedVisit?.signIn, tz)?.format(
      'YYYY-MM-DDTHH:mm:ss',
    ),
    signOut: toLocalByTimezone(selectedVisit?.signOut, tz)?.format(
      'YYYY-MM-DDTHH:mm:ss',
    ),
    scheduleStart: toLocalByTimezone(selectedVisit?.scheduleStart, tz)?.format(
      'YYYY-MM-DDTHH:mm:ss',
    ),
    scheduleEnd: toLocalByTimezone(selectedVisit?.scheduleEnd, tz)?.format(
      'YYYY-MM-DDTHH:mm:ss',
    ),
  };

  // Add visitData fields to initialValues
  visitData.forEach((data) => {
    const { formField, value } = data;
    initialValues[formField.id] = value;
  });

  const handleFieldsChange = (changedFields: FieldChange[]) => {
    const changedField = changedFields[0];
    if (changedField) {
      const { name, value } = changedField;
      const field = Array.isArray(name) ? name[0] : name;

      if (field === 'scheduleStart') {
        const scheduleStart = value;
        if (scheduleStart) {
          const startMoment = opTime(scheduleStart);
          const newEndTime = startMoment.add(1, 'hour');
          form.setFieldsValue({ scheduleEnd: newEndTime });
        }
      } else if (field === 'scheduleEnd') {
        const scheduleEnd = value;
        const scheduleStart = form.getFieldValue('scheduleStart');

        if (scheduleStart && scheduleEnd) {
          const startMoment = opTime(scheduleStart);
          const endMoment = opTime(scheduleEnd);

          if (startMoment.isSameOrAfter(endMoment)) {
            form.setFieldsValue({
              scheduleStart: endMoment.subtract(1, 'hour'),
            });
          }
        }
      }
    }
  };

  const editVisit = async (updatedVisit: EditVisitFormValues) => {
    if (
      updatedVisit.scheduleStart &&
      updatedVisit.scheduleEnd &&
      opTime(updatedVisit.scheduleEnd).isSameOrBefore(
        opTime(updatedVisit.scheduleStart),
      )
    ) {
      notificationService.show(
        'error',
        t('visitors.header.editModal.notification.invalidDateRangeError'),
      );
      return;
    }

    if (
      updatedVisit.signIn &&
      updatedVisit.signOut &&
      opTime(updatedVisit.signOut).isSameOrBefore(opTime(updatedVisit.signIn))
    ) {
      notificationService.show(
        'error',
        t('visitors.header.editModal.notification.invalidDateRangeError'),
      );
      return;
    }
    setLoading(true);

    try {
      const filteredUpdatedVisit = Object.fromEntries(
        Object.entries(updatedVisit).filter(([key]) => isNaN(Number(key))),
      );

      await heliumSend('updateVisit', [orgId, selectedVisit?.id], {
        visitorType: filteredUpdatedVisit.visitorType,
        company: filteredUpdatedVisit.company,
        hostId: filteredUpdatedVisit.hostId ?? -1,
        purpose: filteredUpdatedVisit.purpose,
        signIn: updatedVisit.signIn
          ? opTime
              .tz(opTime(updatedVisit.signIn).format('YYYY-MM-DD HH:mm:ss'), tz)
              .utc()
              .format(DATE_TIME_FORMAT)
          : undefined,
        signOut: updatedVisit.signOut
          ? opTime
              .tz(
                opTime(updatedVisit.signOut).format('YYYY-MM-DD HH:mm:ss'),
                tz,
              )
              .utc()
              .format(DATE_TIME_FORMAT)
          : undefined,
        // Only send scheduleIn/scheduleOut if they exist in the form (Pending status)
        ...(updatedVisit.scheduleStart && updatedVisit.scheduleEnd
          ? {
              scheduleIn: opTime
                .tz(
                  opTime(updatedVisit.scheduleStart).format(
                    'YYYY-MM-DD HH:mm:ss',
                  ),
                  tz,
                )
                .utc()
                .format(DATE_TIME_FORMAT),
              scheduleOut: opTime
                .tz(
                  opTime(updatedVisit.scheduleEnd).format(
                    'YYYY-MM-DD HH:mm:ss',
                  ),
                  tz,
                )
                .utc()
                .format(DATE_TIME_FORMAT),
            }
          : {}),
      });

      await Promise.all(
        Object.entries(updatedVisit)
          .filter(([key]) => !isNaN(Number(key))) // filter if the field is a custom field (custom fields have numeric keys)
          .map(async ([key, field]) => {
            const initialField = initialValues[key];

            if (field !== initialField) {
              const existingData = visitData.find(
                (item) => item.formField.id === Number(key),
              );

              if (field && !initialField && !existingData) {
                try {
                  await heliumSend(
                    'createVisitorData',
                    [orgId, selectedVisit?.id],
                    {
                      fieldId: key,
                      value: field,
                    },
                  );
                } catch {
                  // Failed to add custom field
                }
              } else if (existingData) {
                try {
                  await heliumSend(
                    'updateVisitorData',
                    [orgId, selectedVisit?.id, existingData.id],
                    {
                      fieldId: key,
                      value: field,
                    },
                  );
                } catch {
                  // Failed to update custom field
                }
              }
            }
          }),
      );
      dispatch(fetchVisits({ orgId }));
      dispatch(searchVisitorsToday({ orgId }));
      // Refresh the visitor search results table, which is backed by the
      // `visitorSearch.visitors` slice (populated by `searchVisitors`).
      if (Object.keys(lastSearchParams).length !== 0) {
        dispatch(
          searchVisitors({
            ...lastSearchParams,
            orgId,
            locationId: lastSearchParams.locationId || globalLocationId!,
          }),
        );
      }
      notificationService.show(
        'success',
        t('visitors.header.editModal.notification.updateSuccess'),
      );
    } catch {
      notificationService.show(
        'error',
        t('visitors.header.editModal.notification.updateError'),
      );
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    const isPendingOrNoShow =
      selectedVisit?.visitStatus.id === STATUS.PENDING.id;

    const fieldsToRender = isPendingOrNoShow
      ? Array.from(
          new Map(
            [...customFieldsWorkflow, ...invitationCustomFields].map((f) => [
              f.id,
              f,
            ]),
          ).values(),
        )
      : customFieldsWorkflow;

    const customFieldIds = fieldsToRender.map((field) => field.id);
    // const isPending = selectedVisit?.visitStatus.id === STATUS.PENDING.id;

    return (
      <>
        {fieldsToRender.map((formField) => {
          const rules =
            formField.required === 1 &&
            !(
              isPendingOrNoShow &&
              !invitationCustomFields.some((f) => f.id === formField.id)
            )
              ? [
                  {
                    required: true,
                    message: t(
                      'visitors.header.editModal.form.customFieldRequired',
                      { customFieldName: formField.name },
                    ),
                  },
                ]
              : [];

          if (formField.values) {
            const options = formField.values.map((option) => ({
              label: option.display || option.name,
              value: option.value,
            }));

            return (
              <OpForm.Select
                key={formField.id}
                label={formField.name}
                name={formField.id}
                options={options}
                rules={rules}
                placeholder={
                  formField.placeholder ? formField.placeholder : undefined
                }
                allowClear
              />
            );
          }
          return (
            <OpForm.TextAreaInput
              key={formField.id}
              label={formField.name}
              name={formField.id}
              rows={4}
              rules={rules}
              placeholder={
                formField.placeholder ? formField.placeholder : undefined
              }
            />
          );
        })}

        {visitData
          .filter(
            (visitField) => !customFieldIds.includes(visitField.formField.id),
          ) // Fields not in customFieldsWorkflow
          .map((visitField) => {
            const formField = visitField.formField;

            if (formField.values) {
              const options = formField.values.map((option) => ({
                label: option.display || option.name,
                value: option.value,
              }));

              return (
                <OpForm.Select
                  key={formField.id}
                  label={formField.name}
                  name={formField.id}
                  options={options}
                  placeholder={
                    formField.placeholder ? formField.placeholder : undefined
                  }
                  allowClear
                />
              );
            }
            return (
              <OpForm.Input
                key={formField.id}
                label={formField.name}
                name={formField.id}
                placeholder={
                  formField.placeholder ? formField.placeholder : undefined
                }
              />
            );
          })}
      </>
    );
  };

  const filteredHosts = (
    hasAllVisitorsWrite
      ? hosts
      : hosts.filter((host) => host.id === globalUserId)
  ).filter(
    (host) =>
      !excludedHostIds.includes(host.id) ||
      host.id === selectedVisit?.host?.userId,
  );

  // Define the tabs with the `items` prop
  const tabItems = [
    {
      key: 'details',
      label: t('visitors.header.editModal.detailsTab'),
      children: (
        <>
          {selectedVisit?.visitStatus.id === STATUS.PENDING.id && (
            <>
              <OpForm.Select
                label={t('visitors.header.editModal.form.visitorType')}
                name="visitorType"
                rules={[
                  {
                    required: true,
                    message: t(
                      'visitors.header.editModal.form.visitorTypeRequired',
                    ),
                  },
                ]}
                placeholder={t('visitors.header.editModal.form.visitorType')}
                options={visitorTypes.map((type) => ({
                  label: t(type.languageCode ?? ''),
                  value: type.id,
                }))}
              />
              <OpForm.DatePicker
                format={DATE_TIME_AM_PM_FORMAT}
                showTime
                minuteStep={5}
                name="scheduleStart"
                label={t('visitors.header.editModal.form.scheduleStart')}
                rules={[
                  {
                    required: true,
                    message: t('visitors.header.editModal.form.dateRequired'),
                  },
                ]}
              />
              <OpForm.DatePicker
                format={DATE_TIME_AM_PM_FORMAT}
                showTime
                minuteStep={5}
                name="scheduleEnd"
                label={t('visitors.header.editModal.form.scheduleEnd')}
                rules={[
                  {
                    required: true,
                    message: t('visitors.header.editModal.form.dateRequired'),
                  },
                ]}
              />
            </>
          )}
          {selectedVisit?.visitStatus.id === STATUS.SIGNED_IN.id && ( // Sign In time field is required when status is 'Signed In'
            <OpForm.DatePicker
              format={DATE_TIME_AM_PM_FORMAT}
              showTime
              minuteStep={5}
              name="signIn"
              label={t('visitors.header.editModal.form.signIn')}
              disabledDate={(current) => {
                if (!current) return false;
                const currentDateStr = opTime(current).format('YYYY-MM-DD');
                const currentInTz = opTime.tz(currentDateStr, tz);
                const nowInTz = opTime().tz(tz);
                return currentInTz.isAfter(nowInTz, 'day');
              }}
              disabledTime={getDisabledTime}
              rules={[
                {
                  required: true,
                  message: t('visitors.header.editModal.form.dateRequired'),
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    // Convert value to location timezone and compare with current time
                    const valueStr = opTime(value).format(
                      'YYYY-MM-DD HH:mm:ss',
                    );
                    const valueInTz = opTime.tz(valueStr, tz);
                    const nowInTz = opTime().tz(tz);
                    return valueInTz.isAfter(nowInTz)
                      ? Promise.reject(
                          new Error(
                            t(
                              'visitors.header.editModal.notification.invalidSignInTimeError',
                            ),
                          ),
                        )
                      : Promise.resolve();
                  },
                },
              ]}
            />
          )}

          {selectedVisit?.visitStatus.id === STATUS.SIGNED_OUT.id && (
            <>
              <OpForm.DatePicker
                format={DATE_TIME_AM_PM_FORMAT}
                showTime
                minuteStep={5}
                name="signIn"
                label={t('visitors.header.editModal.form.signIn')}
                disabledDate={(current) => {
                  if (!current) return false;
                  const currentDateStr = opTime(current).format('YYYY-MM-DD');
                  const currentInTz = opTime.tz(currentDateStr, tz);
                  const nowInTz = opTime().tz(tz);
                  return currentInTz.isAfter(nowInTz, 'day');
                }}
                disabledTime={getDisabledTime}
                rules={[
                  {
                    required: true,
                    message: t('visitors.header.editModal.form.dateRequired'),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();

                      const signOut = getFieldValue('signOut');
                      const nowInTz = opTime().tz(tz);
                      const valueDayjs = opTime(value);
                      const valueStr = valueDayjs.format('YYYY-MM-DD HH:mm:ss');
                      const nowStr = nowInTz.format('YYYY-MM-DD HH:mm:ss');

                      if (valueStr > nowStr) {
                        // it cannot be in the future time
                        return Promise.reject(
                          new Error(
                            t(
                              'visitors.header.editModal.notification.invalidSignInTimeError',
                            ),
                          ),
                        );
                      }

                      if (signOut) {
                        const signOutDayjs = opTime(signOut);
                        if (valueDayjs.isSameOrAfter(signOutDayjs)) {
                          // it cannot be after end date
                          return Promise.reject(
                            new Error(
                              t(
                                'visitors.header.editModal.notification.startDateAfterEndDateError',
                              ),
                            ),
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                onChange={() => form.validateFields(['signOut'])}
              />
              <OpForm.DatePicker
                format={DATE_TIME_AM_PM_FORMAT}
                showTime
                minuteStep={5}
                name="signOut"
                label={t('visitors.header.editModal.form.signOut')}
                disabledDate={(current) => {
                  if (!current) return false;
                  const currentDateStr = opTime(current).format('YYYY-MM-DD');
                  const currentInTz = opTime.tz(currentDateStr, tz);
                  const nowInTz = opTime().tz(tz);
                  return currentInTz.isAfter(nowInTz, 'day');
                }}
                disabledTime={getDisabledTime}
                rules={[
                  {
                    required: true,
                    message: t('visitors.header.editModal.form.dateRequired'),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();

                      const signIn = getFieldValue('signIn');
                      const nowInTz = opTime().tz(tz);
                      const valueDayjs = opTime(value);

                      // Convert value to location timezone and compare with current time
                      const valueStr = valueDayjs.format('YYYY-MM-DD HH:mm:ss');
                      const valueInTz = opTime.tz(valueStr, tz);

                      if (valueInTz.isAfter(nowInTz)) {
                        return Promise.reject(
                          new Error(
                            t(
                              'visitors.header.editModal.notification.invalidSignOutTimeError',
                            ),
                          ),
                        );
                      }

                      if (signIn) {
                        const signInDayjs = opTime(signIn);
                        if (valueDayjs.isSameOrBefore(signInDayjs)) {
                          // it cannot be before start date
                          return Promise.reject(
                            new Error(
                              t(
                                'visitors.header.editModal.notification.invalidDateRangeError',
                              ),
                            ),
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                onChange={() => form.validateFields(['signIn'])}
              />
            </>
          )}

          <OpForm.Input
            label={t('visitors.header.editModal.form.company')}
            name="company"
            rules={[
              {
                required:
                  (selectedVisit?.visitStatus.id === STATUS.PENDING.id &&
                    invitationConfig?.visitCompany.required === 1) ||
                  ((selectedVisit?.visitStatus.id === STATUS.SIGNED_IN.id ||
                    selectedVisit?.visitStatus.id === STATUS.SIGNED_OUT.id ||
                    selectedVisit?.visitStatus.id === STATUS.DENIED_ENTRY.id) &&
                    signInWorkflowFields?.visitCompany.required === 1),
                message: t('visitors.header.editModal.form.companyRequired'),
              },
            ]}
          />
          <OpForm.Select
            label={t('visitors.header.editModal.form.host')}
            name="hostId"
            showSearch={{
              filterOption: (input, option) =>
                (option?.label ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase()),
            }}
            placeholder={
              filteredHosts.length === 0
                ? t('visitors.header.editModal.form.hostNoAvailable')
                : t('visitors.header.editModal.form.hostPlaceholder')
            }
            disabled={filteredHosts.length === 0}
            rules={[
              {
                required:
                  (selectedVisit?.visitStatus.id === STATUS.PENDING.id &&
                    invitationConfig?.host.required === 1) ||
                  ((selectedVisit?.visitStatus.id === STATUS.SIGNED_IN.id ||
                    selectedVisit?.visitStatus.id === STATUS.SIGNED_OUT.id ||
                    selectedVisit?.visitStatus.id === STATUS.DENIED_ENTRY.id) &&
                    signInWorkflowFields?.host.required === 1),
                message: t('visitors.header.editModal.form.hostRequired'),
              },
            ]}
            options={filteredHosts.map((host) => ({
              label: host.identity?.fullName || host.identity?.email,
              value: host.id,
            }))}
            allowClear
          />
          <OpForm.TextAreaInput
            rows={3}
            label={t('visitors.header.editModal.form.purpose')}
            name="purpose"
            rules={[
              {
                required:
                  (selectedVisit?.visitStatus.id === STATUS.PENDING.id &&
                    invitationConfig?.visitPurpose.required === 1) ||
                  ((selectedVisit?.visitStatus.id === STATUS.SIGNED_IN.id ||
                    selectedVisit?.visitStatus.id === STATUS.SIGNED_OUT.id ||
                    selectedVisit?.visitStatus.id === STATUS.DENIED_ENTRY.id) &&
                    signInWorkflowFields?.visitPurpose.required === 1),
                message: t('visitors.header.editModal.form.purposeRequired'),
              },
            ]}
          />
        </>
      ),
    },
    ...(visitData.length > 0 ||
    ((selectedVisit?.visitStatus.id === STATUS.SIGNED_IN.id ||
      selectedVisit?.visitStatus.id === STATUS.SIGNED_OUT.id ||
      selectedVisit?.visitStatus.id === STATUS.DENIED_ENTRY.id) &&
      customFieldsWorkflow.length > 0) ||
    (selectedVisit?.visitStatus.id === STATUS.PENDING.id &&
      (invitationCustomFields.length > 0 || customFieldsWorkflow.length > 0))
      ? [
          {
            key: 'additionalInfo',
            label: t('visitors.header.editModal.additionalInfoTab'),
            children: renderFormFields(),
          },
        ]
      : []),
  ];

  return (
    <OpModal
      open={open}
      title={t('visitors.header.editModal.title')}
      okText={t('visitors.header.editModal.save')}
      cancelText={t('visitors.header.editModal.cancel')}
      width={720}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((_values) => {
            form.submit();
            onClose();
          })
          .catch(() => {
            notificationService.show(
              'error',
              t('notification.validationError'),
            );
          });
      }}
      centered
    >
      {isModalLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <OpSpin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          />
        </div>
      ) : (
        <div style={{ overflowY: 'auto', maxHeight: '63vh', padding: '6px' }}>
          <OpForm
            form={form}
            initialValues={initialValues}
            onFieldsChange={handleFieldsChange}
            onSubmit={({ values }) => {
              editVisit(values);
              form.resetFields();
            }}
            hasError={false}
            defaultButtons={false}
          >
            <OpTabs
              activeKey={activeKey}
              onChange={setActiveKey}
              items={tabItems}
            />
          </OpForm>
        </div>
      )}
    </OpModal>
  );
};

export default VisitorsEditModal;
